import { useRef, useState } from 'preact/hooks';
import { ApiError } from '../lib/api';
import { bumpRecordsVersion } from '../lib/recordEditor';
import { claimSelf, ClaimSelfError } from '../lib/claimSelf';
import '../screens/addPerformer.css';

// A8 — THE OWNER BECOMES THE SUBJECT.
//
// ===================================================================
// WHY THIS EXISTS
// ===================================================================
// A7 gave the product a way to set itself up and built exactly one path
// through it: a parent adding OTHER people. An adult signing up for
// themselves — the most common signup — could only appear in their own app by
// inviting themselves at a second email address, accepting the invitation,
// and recording their own date of birth. Decision 6c801e1a says the opposite:
// this product is for ANY actor, guardianship is a path rather than the
// product's identity, and an adult bypasses all of it.
//
// ===================================================================
// WHAT THE ENGINE ALREADY DOES, WHICH IS WHY THIS IS THREE CALLS
// ===================================================================
// `createTenant` seats the owner itself, deriving the job role from the
// manifest's own `seating` (founderJobRole) — so the owner ALREADY holds a
// live membership before this component runs. And because the owner never
// accepts an invite, `requiresMemberAge`'s guard (which runs inside
// acceptInvite's beforeActivate) never runs on them.
//
// SO AN ADULT SETTING UP FOR THEMSELVES NEEDS NO DATE OF BIRTH. That is not a
// gap this file papers over; it is the bypass the decision describes, already
// true in the engine.
//
// No invitation is sent, no email leaves the building, and nobody else is
// involved. That absence is the entire difference between this and
// AddPerformer, and the walk asserts it against the mailer, because it cannot
// be seen on screen.

export interface ClaimYourselfProps {
  tenantId: string;
  /** The performer landed — the household re-reads BEHIND this form. As with
   * AddPerformer, it must NOT unmount this component: A7 lost a receipt in
   * the tick it was written by doing exactly that, three times. */
  onAdded: () => void;
  onClose: () => void;
}

const messageOf = (err: unknown): string =>
  err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);

export function ClaimYourself({ tenantId, onAdded, onClose }: ClaimYourselfProps) {
  const [legalName, setLegalName] = useState('');
  const [professionalName, setProfessionalName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [receipt, setReceipt] = useState<{ line: string; detail?: string; partial?: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Single-flight on a ref, not on `busy` — state is async and two clicks in
   * one tick both read it as false. A7 shipped that bug and a test caught it. */
  const inFlight = useRef(false);

  const ready = legalName.trim().length > 0;

  async function submit(e: Event) {
    e.preventDefault();
    if (inFlight.current || !ready) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setReceipt(null);
    setDone(false);

    const who = professionalName.trim() || legalName.trim();

    // The three calls (who am I → create the performer record, which is me
    // → bind my seat to it) now live in lib/claimSelf.ts, shared with B2's
    // setup name step — this stays the per-step copy around it, since an
    // owner is not row-scoped today and a bind failure is a partial receipt
    // rather than a plain error (the A6 walk is the reason that sentence is
    // written from experience).
    try {
      await claimSelf(tenantId, { legalName, professionalName });
    } catch (err) {
      if (err instanceof ClaimSelfError && err.step === 'seat') {
        setError(`Could not read your own seat: ${messageOf(err.cause)}`);
      } else if (err instanceof ClaimSelfError && err.step === 'bind') {
        setReceipt({
          line: `${who} is on the account, but the seat is not linked to it.`,
          detail: `Everything will be recorded correctly; some views may not find it as yours. ${messageOf(err.cause)}`,
          partial: true,
        });
        bumpRecordsVersion();
        inFlight.current = false;
        setBusy(false);
        return;
      } else if (err instanceof ClaimSelfError) {
        // step === 'create'
        setError(`${who} could not be added: ${messageOf(err.cause)}`);
      } else {
        setError(messageOf(err));
      }
      inFlight.current = false;
      setBusy(false);
      return;
    }

    bumpRecordsVersion();
    setReceipt({ line: `You're set up as ${who}. Nothing was sent to anyone.` });
    inFlight.current = false;
    setBusy(false);
    setDone(true);
    onAdded();
  }

  return (
    <form class="ap" onSubmit={submit}>
      <div class="ap__field">
        <label class="ap__k" for="cy-legal">
          Your name
        </label>
        <input
          id="cy-legal"
          class="ap__input"
          value={legalName}
          placeholder="As it appears on a contract"
          onInput={(e) => setLegalName((e.target as HTMLInputElement).value)}
        />
      </div>

      <div class="ap__field">
        <label class="ap__k" for="cy-pro">
          The name you work under
        </label>
        <input
          id="cy-pro"
          class="ap__input"
          value={professionalName}
          placeholder="Leave empty if it is the same"
          onInput={(e) => setProfessionalName((e.target as HTMLInputElement).value)}
        />
      </div>

      {error ? <div class="ap__error">{error}</div> : null}
      {receipt ? (
        <div class={`ap__receipt${receipt.partial ? ' ap__receipt--partial' : ''}`}>
          <div>{receipt.line}</div>
          {receipt.detail ? <div class="ap__receipt-detail">{receipt.detail}</div> : null}
        </div>
      ) : null}

      <div class="ap__acts">
        {done ? (
          <button type="button" class="ap__go" onClick={onClose}>
            Get started
          </button>
        ) : (
          <>
            <button type="submit" class="ap__go" disabled={!ready || busy}>
              {busy ? 'Setting up…' : 'That\u2019s me'}
            </button>
            <button type="button" class="ap__cancel" onClick={onClose} disabled={busy}>
              Back
            </button>
          </>
        )}
      </div>
    </form>
  );
}
